import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TeamsTeamListPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import TeamsList from 'modules/teams/components/List'
import TeamChartActivationBanner from 'modules/teams/components/TeamChartActivationBanner'
import { TeamsShiftLocationProvider } from 'modules/teams/components/teamsShiftLocationContext'
import teamsAnalytics from 'modules/teams/store/collections/teamsAnalytics'
import teamsShiftLocation from 'modules/teams/store/collections/teamsShiftLocation'
import { WORKING_LOCATION_RANGE_DAYS } from 'modules/teams/store/models/TeamShiftLocationAnalytics'

const fetchLocationAnalytics = (hasClockInLocationEnabled: boolean, teamIds: string[]) => {
  const days = WORKING_LOCATION_RANGE_DAYS
  const thirtyDaysAgo = moment().subtract(days, 'day')
  const hasLocationEnabled = hasClockInLocationEnabled

  if (!hasLocationEnabled || teamIds.length === 0) {
    return Promise.resolve()
  }

  return teamsShiftLocation.fetch({
    data: { from_date: thirtyDaysAgo.toDate(), ids: teamIds, group_by: 'team' },
  })
}

const TeamsListPage = () => {
  const { company, employee } = useCurrentSession()
  const { data, loading } = useQuery(TeamsTeamListPageDocument, {
    variables: {
      companyId: company.id,
      employeeId: employee.id,
      featureNames: ['teams_insights'],
    },
  })
  const teams = data?.teams.teams
  const canEmployeeSeeAggregatedPersonalInfo =
    data?.employees._policies.employeeSeeAggregatedPersonalInfo.can ?? false
  const hasTeamsInsightsFeature =
    data?.features?.featuresConnection?.edges.find((edge) => edge.node.name === 'teams_insights')
      ?.node.enabled ?? false
  const hasClockInLocationEnabled =
    data?.attendance?.employeesSettingsConnection?.edges[0]?.node?.enableClockInLocation ?? false

  const teamsIds = teams?.map((team) => team.id) || []

  const canCreateTeams = Boolean(data?.teams._policies.teamCreate.can)

  const { loading: loadingAnalytics } = useAsyncData(() => {
    if (!teamsIds.length) {
      return Promise.resolve()
    }

    return Promise.all([
      teamsAnalytics.fetch(),
      fetchLocationAnalytics(hasClockInLocationEnabled, teamsIds),
    ])
  }, [`${teamsIds}`])

  return (
    <TeamsShiftLocationProvider collection={teamsShiftLocation}>
      <ResponsivePage.Body loading={loading} error={null} showHeader={false}>
        <Box gap="s24">
          {canCreateTeams && <TeamChartActivationBanner companyId={company.id} />}
          <TeamsList
            loadingAnalytics={loadingAnalytics}
            teams={teams || []}
            canEmployeeSeeAggregatedPersonalInfo={canEmployeeSeeAggregatedPersonalInfo}
            hasTeamsInsightsFeature={hasTeamsInsightsFeature}
            hasClockInLocationEnabled={hasClockInLocationEnabled}
          />
        </Box>
        <Outlet />
      </ResponsivePage.Body>
    </TeamsShiftLocationProvider>
  )
}

export default observer(TeamsListPage)
